// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import {ChannelDebugModal} from '@app/features/devtools/components/debug/ChannelDebugModal';
import {GuildDebugModal} from '@app/features/devtools/components/debug/GuildDebugModal';
import {GuildMemberDebugModal} from '@app/features/devtools/components/debug/GuildMemberDebugModal';
import {UserDebugModal} from '@app/features/devtools/components/debug/UserDebugModal';
import type {Guild} from '@app/features/guild/models/Guild';
import type {GuildMember} from '@app/features/member/models/GuildMember';
import {DebugIcon} from '@app/features/ui/action_menu/ContextMenuIcons';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import type {User} from '@app/features/user/models/User';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

interface BaseDebugMenuItemProps {
	onClose: () => void;
}
type DebugUserMenuItemProps = BaseDebugMenuItemProps & {
	user: User;
};
export const DebugUserMenuItem: React.FC<DebugUserMenuItemProps> = observer(({user, onClose}) => {
	const {t} = useLingui();
	const handleDebug = useCallback(() => {
		ModalCommands.push(modal(() => <UserDebugModal title={t`User Debug`} user={user} />));
		onClose();
	}, [user, onClose]);
	return (
		<MenuItem icon={<DebugIcon />} onClick={handleDebug}>
			{t`Debug User`}
		</MenuItem>
	);
});
type DebugChannelMenuItemProps = BaseDebugMenuItemProps & {
	channel: Channel;
};
export const DebugChannelMenuItem: React.FC<DebugChannelMenuItemProps> = observer(({channel, onClose}) => {
	const {t} = useLingui();
	const handleDebug = useCallback(() => {
		ModalCommands.push(modal(() => <ChannelDebugModal title={t`Channel Debug`} channel={channel} />));
		onClose();
	}, [channel, onClose]);
	return (
		<MenuItem icon={<DebugIcon />} onClick={handleDebug}>
			{t`Debug Channel`}
		</MenuItem>
	);
});
type DebugGuildMenuItemProps = BaseDebugMenuItemProps & {
	guild: Guild;
};
export const DebugGuildMenuItem: React.FC<DebugGuildMenuItemProps> = observer(({guild, onClose}) => {
	const {t} = useLingui();
	const handleDebug = useCallback(() => {
		ModalCommands.push(modal(() => <GuildDebugModal title={t`Community Debug`} guild={guild} />));
		onClose();
	}, [guild, onClose]);
	return (
		<MenuItem icon={<DebugIcon />} onClick={handleDebug}>
			{t`Debug Community`}
		</MenuItem>
	);
});
type DebugGuildMemberMenuItemProps = BaseDebugMenuItemProps & {
	member: GuildMember;
};
export const DebugGuildMemberMenuItem: React.FC<DebugGuildMemberMenuItemProps> = observer(({member, onClose}) => {
	const {t} = useLingui();
	const handleDebug = useCallback(() => {
		ModalCommands.push(modal(() => <GuildMemberDebugModal title={t`Community Member Debug`} member={member} />));
		onClose();
	}, [member, onClose]);
	return (
		<MenuItem icon={<DebugIcon />} onClick={handleDebug}>
			{t`Debug Member`}
		</MenuItem>
	);
});
